import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useEffect, useState } from "react";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";

import { Feather, FontAwesome5, MaterialIcons } from "@expo/vector-icons";
import { observer, Observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import IconEmptyView from "../Components/IconEmptyView";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import PredefinedExerciseLayoutView from "../Exercise/PredefinedExerciseLayoutView";
import SearchBar from "../Components/SearchBar";
import { ProtocolTitles } from "../../Resources/Constants";
import { useToast } from "../contexts/ToastContext";
import { shouldShowProtocolSlotUi } from "../platform/guards/coachBlockDashboardVisibility";
const EditWorkoutView = observer(({ workout, coachLibrary = null, coach }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { showToast } = useToast();
  const coachAux = coach ? coach : workout?.block?.client?.coach;

  const slotUiCoach =
    workout?.coach ?? workout?.block?.coach ?? workout?.block?.client?.coach;
  const showProtocolSlotUi = shouldShowProtocolSlotUi(slotUiCoach);

  const ExercisesToAddListView = () => {
    const moveExercise = (fromIndex, toIndex) => {
      const exercises = Array.from(
        workout.exercisesToAdd.sort((a, b) => a.order - b.order)
      );
      const [movedExercise] = exercises.splice(fromIndex, 1);
      exercises.splice(toIndex, 0, movedExercise);

      exercises.forEach((ex, idx) => {
        ex.setProperty("order", idx);
      });

      workout.setExercisesToAdd(exercises);
    };
    const SelectedExerciseRow = ({ exercise, index, moveExercise }) => {
      const deleteExercise = () => {
        workout.setExercisesToAdd(
          workout.exercisesToAdd.filter((ex) => ex !== exercise)
        );
      };

      const DeleteButton = () => {
        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            onPress={deleteExercise}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ opacity: 1, padding: 2 }}
          >
            <Feather
              name="x"
              size={18}
              color={isHovered ? Colors.redColor : Colors.blackColor}
            />
          </TouchableOpacity>
        );
      };

      const [isHovered, setIsHovered] = useState(false);

      const ref = React.useRef(null);

      const [, drop] = useDrop({
        accept: "exercise",
        hover(item, monitor) {
          if (!ref.current) return;
          const dragIndex = item.index;
          const hoverIndex = index;
          if (dragIndex === hoverIndex) return;

          // Determine rectangle on screen
          const hoverBoundingRect = ref.current.getBoundingClientRect();

          // Get vertical middle
          const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

          // Determine mouse position
          const clientOffset = monitor.getClientOffset();

          // Get pixels to the top
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;

          // Only move when the mouse has crossed half of the item's height
          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

          // Move the item
          moveExercise(dragIndex, hoverIndex);

          // Note: Mutate the monitor item to update its index
          item.index = hoverIndex;
        },
      });

      const [{ isDragging }, drag, dragPreview] = useDrag({
        type: "exercise",
        item: { type: "exercise", id: exercise.id, index },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      });

      drag(drop(ref));

      useEffect(() => {
        dragPreview(getEmptyImage(), { captureDraggingState: false });
      }, [dragPreview]);

      return (
        <View
          style={[
            GlobalStyles.HStack,
            {
              alignItems: "center",
              width: "100%",
            },
          ]}
        >
          <TouchableOpacity
            onPress={() => {
              workout.setSelectedExerciseToAdd(exercise);
              if (workout?.block?.client) {
                workout.block.client.setProperty("showingInfoView", true);
              } else if (workout?.block?.coach) {
                workout?.block.coach.setProperty("showingInfoView", true);
              } else {
                coachAux.setProperty("showingInfoView", true);
              }
            }}
            style={{
              maxWidth: screenSize === "small" ? "75%" : "85%",
              backgroundColor: isDragging
                ? "rgba(63, 123, 141, 0.1)"
                : "transparent",
              borderRadius: isDragging ? 12 : 0,
            }}
            ref={ref}
          >
            <View
              style={[
                GlobalStyles.HStack,
                {
                  alignItems: "center",
                  paddingLeft: 4,
                  paddingRight: 8,
                  paddingVertical: 8,
                  gap: 4,
                },
              ]}
            >
              <MaterialIcons
                name="drag-indicator"
                size={14}
                color={Colors.grayColor}
                style={{
                  opacity: 1,
                  cursor: "grab",
                }}
              />
              <Observer>
                {() => (
                  <View style={{ gap: 2, flexShrink: 1, flex: 1, minWidth: 0 }}>
                    <Text
                      style={[Fonts.bold14, { flexShrink: 1 }]}
                      numberOfLines={1}
                      onMouseEnter={() => !isMobile && setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                    >
                      {exercise.getName(t, i18n.language)}
                    </Text>
                    {exercise.protocolSlot && showProtocolSlotUi && (
                      <Text
                        numberOfLines={1}
                        style={{
                          width: "100%",
                          fontSize: 11,
                          fontWeight: "500",
                          color: Colors.grayColor,
                        }}
                      >
                        {t(exercise.protocolSlot)}
                      </Text>
                    )}
                  </View>
                )}
              </Observer>
            </View>
          </TouchableOpacity>
          {!isDragging && <DeleteButton />}
        </View>
      );
    };
    return (
      <Observer>
        {() => (
          <View
            style={{
              gap: 6,
            }}
          >
            {workout.exercisesToAdd.length > 0 ? (
              <>
                {workout.exercisesToAdd
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((exercise, index) => (
                    <SelectedExerciseRow
                      key={exercise.id}
                      exercise={exercise}
                      index={index}
                      moveExercise={moveExercise}
                    />
                  ))}
              </>
            ) : (
              <IconEmptyView
                iconLibrary={"MaterialCommunityIcons"}
                mainText={"selectExercisesTitle-string"}
                subText={"selectExercisesText-string"}
                iconName={"magnify-plus-outline"}
                marginTop={49}
                marginBottom={8}
              />
            )}
          </View>
        )}
      </Observer>
    );
  };

  const SlotExercisesLayoutView = observer(({ basicExercise }) => {
    const SlotExerciseButton = observer(({ slotExercise, index }) => {
      const [isHovered, setIsHovered] = useState(false);

      const isUsed =
        workout.exercisesToAdd.some(
          (ex) => ex.protocolSlot === slotExercise.protocolSlot
        ) ||
        (workout.block &&
          workout.block
            .getAllExercisesFromOtherWorkoutsOfWeek(workout)
            .some((ex) => ex.protocolSlot === slotExercise.protocolSlot));

      const select = () => {
        if (!isUsed) {
          slotExercise.duplicateSlotExercise(workout);
        }
      };

      const textOfSlotF =
        slotExercise.protocolSlot === ProtocolTitles.PRIMARY_SQUAT ||
        slotExercise.protocolSlot === ProtocolTitles.PRIMARY_BENCH_PRESS
          ? "primaryF-string"
          : slotExercise.protocolSlot === ProtocolTitles.SECONDARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.SECONDARY_BENCH_PRESS
          ? "secondaryF-string"
          : slotExercise.protocolSlot === ProtocolTitles.TERTIARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.TERTIARY_BENCH_PRESS
          ? "tertiaryF-string"
          : slotExercise.protocolSlot === ProtocolTitles.QUATERNARY_SQUAT ||
            slotExercise.protocolSlot === ProtocolTitles.QUATERNARY_BENCH_PRESS
          ? "quaternaryF-string"
          : "quinaryF-string";
      const textOfSlotM =
        slotExercise.protocolSlot === ProtocolTitles.PRIMARY_DEADLIFT
          ? "primaryM-string"
          : slotExercise.protocolSlot === ProtocolTitles.SECONDARY_DEADLIFT
          ? "secondaryM-string"
          : slotExercise.protocolSlot === ProtocolTitles.TERTIARY_DEADLIFT
          ? "tertiaryM-string"
          : slotExercise.protocolSlot === ProtocolTitles.QUATERNARY_DEADLIFT
          ? "quaternaryM-string"
          : "quinaryM-string";

      return (
        <TouchableOpacity
          onPress={select}
          style={{ maxWidth: "100%" }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={[
              styles.AttributeButton,
              {
                backgroundColor: isUsed
                  ? Colors.whiteColor
                  : isHovered
                  ? "rgba(63, 123, 141, 0.65)"
                  : "transparent",
                borderColor: isUsed
                  ? Colors.grayColor
                  : isHovered
                  ? "rgba(63, 123, 141, 0.65)"
                  : Colors.grayColor,
                opacity: isUsed ? (isHovered ? 0.4 : 0.5) : 1,
              },
            ]}
          >
            <View style={[GlobalStyles.HStack, { alignItems: "center" }]}>
              <Text
                key={index}
                style={{
                  fontSize: 12,
                  padding: 5,
                  color: isUsed
                    ? Colors.grayColor
                    : isHovered
                    ? "white"
                    : Colors.grayColor,
                  width: "100%",
                }}
              >
                {slotExercise.getName(t, i18n.language)}
                {slotExercise.getSecondProtocol &&
                  ` + ${slotExercise.getSecondProtocol.getName(
                    t,
                    i18n.language
                  )}`}{" "}
                {basicExercise === "squat-string" ||
                basicExercise === "benchPress-string"
                  ? `(${t(textOfSlotF)})`
                  : `(${t(textOfSlotM)})`}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    });

    const filtered = workout.block.slotsForLayout(basicExercise);

    return (
      <>
        {filtered.length > 0 &&
          (coachAux.searchTermPred === "" ||
            (filtered && filtered.length > 0)) && (
            <View style={{ marginTop: 4 }}>
              <Text style={[Fonts.bold14, { color: Colors.grayColor }]}>
                {t("exerciseSlots-string", { name: t(basicExercise) })}
              </Text>

              <View style={[styles.AttributeFlex, { gap: 6, marginTop: 8 }]}>
                {filtered.map((slotExercise, index) => (
                  <SlotExerciseButton
                    slotExercise={slotExercise}
                    index={index}
                    key={index}
                  />
                ))}
              </View>
            </View>
          )}
      </>
    );
  });

  const Buttons = ({ bool }) => {
    const text = bool ? "save-string" : "cancel-string";

    const [isHovered, setIsHovered] = useState(false);

    const Save = () => {
      workout.setExercises();
      workout.setProperty("showingEditWorkout", false);
      if (coachAux && coachAux.onboardingShowBlock) {
        coachAux.setProperty("onboardingShowBlock", false);
        coachAux.updateCoachPublicProfile({
          onboarding_show_block: false,
        });

        showToast("step2Completed-string", {
          type: "info",
          description: "step2CompletedDescription-string",
        });
      }
    };
    const Cancel = () => {
      workout.setExercisesToAdd();
      workout.setProperty("showingEditWorkout", false);
    };

    return bool ? (
      <TouchableOpacity
        onPress={Save}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ marginRight: 10 }}
      >
        <View
          style={{
            backgroundColor: Colors.bubbleColor,
            borderRadius: 12,
            borderWidth: 1,
            justifyContent: "center",
            borderColor: Colors.bubbleColor,
            width: 76,
            justifyContent: "center",
            alignItems: "center",
            opacity: isHovered ? 0.8 : 1,
          }}
        >
          <Text
            style={{
              textAlignVertical: "center",
              textAlign: "center",
              color: Colors.whiteColor,
              paddingVertical: 5,
            }}
          >
            {t(text)}
          </Text>
        </View>
      </TouchableOpacity>
    ) : (
      <TouchableOpacity
        onPress={Cancel}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ marginRight: 10 }}
      >
        <View
          style={{
            backgroundColor: isHovered ? Colors.redColor : "transparent",
            borderRadius: 12,
            borderWidth: 1,
            justifyContent: "center",
            borderColor: "red",
            width: 76,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              textAlignVertical: "center",
              textAlign: "center",
              color: isHovered ? "white" : Colors.redColor,
              paddingVertical: 5,
            }}
          >
            {t(text)}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const WorkoutRow = observer(({ libraryWorkout }) => {
    const action = () => {
      libraryWorkout.addLibraryWorkout(workout);
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={{
          width: `calc((100% - ${screenSize === "small" ? 8 : 24}px) / ${
            screenSize === "small" ? 2 : 3
          })`,
        }}
      >
        <TouchableOpacity
          onPress={action}
          style={[
            GlobalStyles.touchableRow,
            {
              borderWidth: 1,
              borderColor: isHovered
                ? "rgba(0, 0, 0, 0.5)"
                : Colors.borderColor,
            },
          ]}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View style={{ alignItems: "flex-start", flex: 1 }}>
            <Text
              numberOfLines={2}
              style={[Fonts.medium13, { width: "100%", marginBottom: 8 }]}
            >
              {libraryWorkout.name}
            </Text>
            <View style={GlobalStyles.HStack}>
              <FontAwesome5
                name="clipboard-list"
                size={12}
                color={Colors.bubbleColor}
                style={GlobalStyles.rowIcon}
              />
              <Text
                style={{
                  ...GlobalStyles.rowSubText,
                  paddingRight: 0,
                  fontSize: 12,
                }}
                numberOfLines={1}
              >
                {libraryWorkout.exercises.length === 1
                  ? t("exerciseCountOne-string", {
                      count: libraryWorkout.exercises.length,
                    })
                  : t("exerciseCount-string", {
                      count: libraryWorkout.exercises.length,
                    })}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  });

  const ShowLibraryButton = observer(() => {
    const [isHovered, setIsHovered] = useState(false);

    const showLibrary = () => {
      workout.setProperty(
        "showingCoachWorkouts",
        !workout.showingCoachWorkouts
      );
    };
    return (
      <TouchableOpacity
        onPress={showLibrary}
        style={[
          GlobalStyles.HStack,
          {
            paddingLeft: 6,
            paddingRight: 8,
            marginLeft: 12,
            flexShrink: 1,
            opacity: isHovered ? 0.5 : 1,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text
          style={[
            Fonts.medium14,
            {
              color: Colors.bubbleColor,
            },
          ]}
        >
          {workout.showingCoachWorkouts
            ? t("showPredefinedExercises-string")
            : t("showWorkoutLibrary-string")}
        </Text>
      </TouchableOpacity>
    );
  });
  return (
    <View
      style={{
        flex: 1,
      }}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: "white",
          borderRadius: 14,
        }}
      >
        <View
          style={[
            GlobalStyles.HStack,
            {
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              borderBottomWidth: 0.2,
              borderColor: Colors.borderColor,
              backgroundColor: Colors.lightGrayColor,
              justifyContent: "flex-end",
              height: 40,
            },
          ]}
        >
          <Buttons bool={false} />
          <Buttons bool={true} />
        </View>
        <View style={{ marginHorizontal: 8, marginTop: 12 }}>
          <ExercisesToAddListView />
        </View>

        <View style={{ flex: 1, margin: 12, gap: 12 }}>
          {!workout?.coach && showProtocolSlotUi && (
            <View style={{ gap: 8, marginBottom: 4 }}>
              <SlotExercisesLayoutView basicExercise={"squat-string"} />
              <SlotExercisesLayoutView basicExercise={"benchPress-string"} />
              <SlotExercisesLayoutView basicExercise={"deadlift-string"} />
            </View>
          )}

          <View style={[GlobalStyles.HStack, { width: "100%" }]}>
            {!workout?.coach ? (
              <>
                <View style={{ maxWidth: "55%" }}>
                  <SearchBar
                    text={
                      workout.showingCoachWorkouts
                        ? coachAux.searchTermWorkoutLibrary
                          ? coachAux.searchTermWorkoutLibrary
                          : ""
                        : coachAux.searchTermPred
                        ? coachAux.searchTermPred
                        : ""
                    }
                    onChangeText={(text) => {
                      if (workout.showingCoachWorkouts) {
                        coachAux.setProperty("searchTermWorkoutLibrary", text);
                      } else {
                        coachAux.setSearchTermPred(text);
                      }
                    }}
                    dark={false}
                    placeholder={
                      workout.showingCoachWorkouts
                        ? "searchWorkout-string"
                        : "searchExercise-string"
                    }
                  />
                </View>

                <ShowLibraryButton />
              </>
            ) : (
              <SearchBar
                text={coachAux.searchTermPred}
                onChangeText={(text) => {
                  coachAux.setSearchTermPred(text);
                }}
                dark={false}
                placeholder={"searchExercise-string"}
              />
            )}
          </View>

          <Observer>
            {() => (
              <>
                {workout.showingCoachWorkouts && !workout.coach ? (
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      flexWrap: "wrap",
                      gap: screenSize === "small" ? 8 : 12,
                    }}
                  >
                    {coachAux &&
                      coachAux
                        .getFilteredWorkoutLibrary()
                        .slice()
                        .map((libraryWorkout) => {
                          return (
                            <WorkoutRow
                              libraryWorkout={libraryWorkout}
                              key={libraryWorkout.id}
                            />
                          );
                        })}
                  </View>
                ) : (
                  <>
                    <PredefinedExerciseLayoutView
                      coach={coachAux}
                      workout={workout}
                      block={workout.block}
                    />
                  </>
                )}
              </>
            )}
          </Observer>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#EFF4F5",
  },
  AttributeFlex: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  AttributeButton: {
    borderRadius: 8,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
});

export default EditWorkoutView;
